import { RawWorkflowListFilters, Workflow, WorkflowPayload } from 'features/workflows/types'
import { Provider } from '@cloud-native/workflows-engine'
import { dataSyncManager } from '../../DataSyncManager'
import { WorkflowEngineClient } from '../WorkflowEngineClient/WorkflowEngineClient'
import { workflowEngineClient } from '../WorkflowEngineClient'
import { CommonPrivacy, deleteRequest, getRequest, patchRequest, postRequest } from '../../../../common'
import { getClient } from 'features/common/utils/clientParameter'
import { sanitizeCreateWorkflow } from '../../../model/normalizers'
import { DEFAULT_PAGE_SIZE } from '../../../constants'
import {
  CreateWorkflowModel,
  DuplicateWorkflowModel,
  ListQueryParams,
  LockRequestModel,
  LockResponseModel,
  NotifySharedUserModel,
  TakeOverActionModel,
  UpdatePrivacyModel
} from './types'

// TODO create abstraction layer for toolbox & WE engine. Toolbox & WE Engine are classes, we should have references for them.
export class WorkflowClient {
  constructor(private readonly apiUrl: string, private readonly workflowEngineClient: WorkflowEngineClient) {}

  async fetchTablePermissions(workflow: Workflow) {
    const workflowEngineConnection = workflowEngineClient.getWorkflowEngineConnection(workflow)

    const query = workflowEngineConnection.getSourceTablePermissionsQuery(
      this.workflowEngineClient.getWorkflowEngine(workflow)
    )

    if (!query) {
      return undefined
    }

    const response = await dataSyncManager.sync(query)

    if (workflow.config.connectionProvider === Provider.Redshift) {
      return (response as Array<Record<string, string>>).map((r) => r.noaccess)
    }

    if (response?.[0]) {
      const noAccessKey = Object.keys(response[0]).find((key) => RegExp('^noAccess$', 'i').exec(key))
      return noAccessKey && response[0][noAccessKey]
    }

    return undefined
  }

  getById(id: string): Promise<WorkflowPayload> {
    return getRequest(`${this.apiUrl}/${id}?client=${getClient()}`)
  }

  getList({ pageParam = 0, queryKey: [, queryFilters, pageSize] }: ListQueryParams) {
    const {
      search,
      procedure,
      order: { order_by, order_direction },
      permissions,
      tags
    } = queryFilters as RawWorkflowListFilters

    const params = new URLSearchParams({
      page: `${pageParam + 1}`,
      page_size: `${pageSize ?? DEFAULT_PAGE_SIZE}`,
      ...(search && { search }),
      ...(procedure && { procedure: 'true' }),
      ...(tags?.length && { tags: JSON.stringify(tags) }),
      order_by,
      order_direction,
      ...(permissions?.user_id && { user_id: permissions.user_id }),
      ...(permissions?.privacy && { privacy: permissions.privacy as unknown as string }),
      client: getClient()
    }).toString()

    return getRequest(`${this.apiUrl}?${params}`)
  }

  async save(workflow: WorkflowPayload): Promise<Workflow> {
    // TODO: New Model Initiative should define proper types for workflow/node/edges when
    // (i) in state
    // (ii) on the wire (HTTP)
    // and (de)serializers for WorkflowState <--> WorkflowPayload
    const config = {
      ...workflow.config,
      edges: workflow.config.edges.map((edge) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { animated, className, ...rest } = edge // Remove animated and className properties
        return rest
      })
    }

    const requestBody: {
      config: Record<string, unknown>
      tags?: string[]
    } = {
      config: config,
      tags: workflow.tags
    }

    return patchRequest(`${this.apiUrl}/${workflow.id}`, {
      ...requestBody,
      client: getClient()
    })
  }

  updateTempLocation({ workflowId, tempLocationId }: { workflowId: string; tempLocationId: string }) {
    return patchRequest(`${this.apiUrl}/${workflowId}`, { tempLocation: { id: tempLocationId }, client: getClient() })
  }

  duplicate(payload: DuplicateWorkflowModel) {
    return postRequest(`${this.apiUrl}/${payload.workflowId}/duplicate`, {
      connectionId: payload.connectionId,
      client: getClient()
    })
  }

  create(payload: CreateWorkflowModel) {
    return postRequest(this.apiUrl, {
      ...sanitizeCreateWorkflow(payload.workflow as Workflow),
      tempLocationId: payload.tempLocationId,
      client: getClient()
    })
  }

  delete(id: string) {
    return deleteRequest(`${this.apiUrl}/${id}?client=${getClient()}`)
  }

  updatePrivacy(payload: UpdatePrivacyModel) {
    return patchRequest(`${this.apiUrl}/${payload.workflowId}/privacy`, {
      privacy: payload.privacy,
      groupIds: payload.privacy === CommonPrivacy.Shared ? payload.selectedGroups?.map((group) => group.id) : undefined,
      userIds: payload.privacy === CommonPrivacy.Shared ? payload.users?.map((user) => user.id) : undefined,
      client: getClient(),
      sharingScope: payload.sharingScope
    })
  }

  updateCollaborative({ workflowId, collaborative }: { workflowId: string; collaborative: boolean }) {
    return patchRequest(`${this.apiUrl}/${workflowId}`, { collaborative, client: getClient() })
  }

  notifySharedUser({ workflowId, userId }: NotifySharedUserModel) {
    return postRequest(`${this.apiUrl}/${workflowId}/privacy/notify-user`, { userId })
  }

  lock({ workflowId, ...body }: LockRequestModel): Promise<LockResponseModel> {
    return postRequest(`${this.apiUrl}/${workflowId}/lock`, { ...body })
  }

  unlock({ workflowId }: { workflowId: string }): Promise<unknown> {
    return postRequest(`${this.apiUrl}/${workflowId}/unlock`, {})
  }

  unlockOnUnload({ workflowId, token }: { workflowId: string; token: string }) {
    if ('sendBeacon' in navigator) {
      // actually, you can't send headers, so we need to pass token in URL method ...
      navigator.sendBeacon(`${this.apiUrl}/${workflowId}/unlock?access_token=${token}`)
    } else {
      // attempt old async XMLHttpRequest, but
      try {
        const r = new XMLHttpRequest()
        r.open('POST', `${this.apiUrl}/${workflowId}/unlock`, false)
        r.setRequestHeader(`Authorization`, `Bearer: ${token}`)
        r.send(null)
      } catch (err) {
        // ignore it, we can't do anything, we're already unloading
      }
    }
  }

  takeOverAction({ workflowId, action }: TakeOverActionModel): Promise<void> {
    return postRequest(`${this.apiUrl}/${workflowId}/take-over/${action}`, {})
  }
}
